<template>
  <div class="cn-service-centers">
    <div class="cn-service-centers__left">
      <div class="cn-service-centers__sidebar">
        <div class="cn-service-centers__form">
          <UiSelect
            v-model="selectedCountry"
            :options="countries"
            :placeholder="$t('choose_country')"
            :label="$t('country')"
          />
          <UiSelect
            v-model="selectedCity"
            :disabled="citiesSelectDisable"
            :options="cities"
            :placeholder="$t('choose_city')"
            :label="$t('city')"
            clearable
          />
        </div>
        <ul class="cn-service-centers__centers">
          <li
            v-for="(service, i) in filteredServiceCenters"
            :key="i"
            ref="markerRefs"
            class="cn-service-centers__center"
            :class="{
              'cn-service-centers__center--active': activeMarker === i,
            }"
            @click="setActiveMarker(i)"
          >
            <UiIcon
              name="location-alt"
              class="cn-service-centers__center-icon"
            />
            <div class="cn-service-centers__center-text">
              <h3 class="cn-service-centers__center-title">
                {{ service.name }}
              </h3>
              <p class="cn-service-centers__center-address">
                {{ service.address }}
              </p>
              <p
                v-for="(phone, j) in service.phones"
                :key="j"
                class="cn-service-centers__center-phones"
              >
                <a :href="`tel:${phone}`">
                  {{ phone }}
                </a>
              </p>
            </div>
          </li>
        </ul>
      </div>
    </div>
    <div class="cn-service-centers__right">
      <client-only>
        <AppMap v-model:active-marker="activeMarker" :markers="markers" />
      </client-only>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import AppMap from '@/components/AppMap';
import UiSelect from '@/components/ui/UiSelect';
import UiIcon from '@/components/ui/UiIcon';

interface Props {
  serviceCenters: any[];
  countries: any[];
  cities: any[];
  citiesLoading?: boolean;
}
const props = defineProps<Props>();
const emit = defineEmits<{
  (e: 'change-country', data: any): void;
}>();
const selectedCountry = ref(null);
const selectedCity = ref(null);
const activeMarker = ref<number | null>(null);
const markerRefs = ref<any>([]);
const setActiveMarker = (i: number) => {
  activeMarker.value = i;
};
const citiesSelectDisable = computed(
  () =>
    selectedCountry.value === null ||
    props.citiesLoading ||
    (!props.citiesLoading && !props.cities.length),
);

const filteredServiceCenters = computed(() => {
  let result = [];
  if (selectedCountry.value !== null && !selectedCity.value) {
    result = props.serviceCenters.filter(
      (s) => selectedCountry.value === s.countryId,
    );
  } else if (selectedCity.value !== null) {
    result = props.serviceCenters.filter(
      (s) => selectedCity.value === s.cityId,
    );
  } else {
    result = props.serviceCenters;
  }
  return result;
});

const markers = computed(() =>
  filteredServiceCenters.value.map((s) => {
    return {
      title: s.name,
      position: {
        lat: s.lat,
        lng: s.lng,
      },
    };
  }),
);

watch(selectedCountry, (newValue) => {
  selectedCity.value = null;
  activeMarker.value = null;
  emit('change-country', newValue);
});

watch(selectedCity, () => {
  activeMarker.value = null;
});

watch(activeMarker, (value) => {
  if (!value) return;
  const serviceElement = markerRefs.value[value];
  serviceElement.scrollIntoView({
    behavior: 'smooth',
    block: 'nearest',
  });
});
</script>

<style lang="scss">
.cn-service-centers {
  --ab-canyon-service-centers-sidebar-color-bg: var(
    --ab-canyon-color-neutral-light
  );
  --ab-canyon-service-centers-centers-color-border: var(
    --ab-canyon-color-neutral
  );
  --ab-canyon-service-centers-center-color-bg-active: var(
    --ab-canyon-color-light
  );
  --ab-canyon-service-centers-address-color-text: var(
    --ab-canyon-color-neutral-dark
  );
  --ab-canyon-service-centers-title-color-text: var(
    --ab-canyon-color-neutral-dark
  );

  flex-direction: column;
  display: flex;
  margin-top: var(--size-xl);
  @media (min-width: 992px) {
    flex-direction: row;
  }

  &__left {
    width: 100%;
    height: 600px;
    @media (min-width: 992px) {
      max-width: 350px;
    }
  }

  &__sidebar {
    background-color: var(--ab-canyon-service-centers-sidebar-color-bg);
    padding: var(--size-base);
    row-gap: calc(var(--size-base) / 2);
    flex-direction: column;
    height: 100%;
    display: flex;
  }
  &__form {
    > :not([hidden]) {
      ~ :not([hidden]) {
        margin-top: var(--size-xl);
      }
    }
  }
  &__centers {
    overflow-y: auto;
    > .cn-service-centers {
      &__center {
        ~ :not([hidden]) {
          border-top-width: 1px;
          border-color: var(--ab-canyon-service-centers-centers-color-border);
        }
      }
    }
  }
  &__center {
    padding-top: var(--size-base);
    padding-bottom: var(--size-base);
    column-gap: calc(var(--size-base) / 2);
    display: flex;
    &--active {
      background-color: var(--ab-canyon-service-centers-center-color-bg-active);
    }
  }
  &__center-icon {
    color: var(--ab-canyon-service-centers-title-color-text);
    width: var(--size-xl);
    height: var(--size-xl);
  }
  &__center-title {
    color: var(--ab-canyon-service-centers-title-color-text);
    font-weight: 700;
    font-size: 1.125rem;
    line-height: 1.75rem;
  }
  &__center-address {
    color: var(--ab-canyon-service-centers-address-color-text);
  }
  &__center-phones {
    color: var(--ab-canyon-service-centers-address-color-text);
    a {
      text-decoration: underline;
      &:hover {
        text-decoration: none;
      }
    }
  }
  &__right {
    width: 100%;
    height: 400px;
    display: none;
    @media (min-width: 992px) {
      height: 600px;
      display: block;
    }
  }
}
</style>
